import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tournamentsAPI } from '../services/api';
import { Tournament as TournamentType, TournamentParticipant } from '../types';
import './Tournament.css';

type DrawMatch = {
  id: string;
  roundIndex: number;
  slotIndex: number;
  p1: string;
  p2: string;
  scoreLabel: string;
  completed: boolean;
  winner: 'p1' | 'p2' | null;
  sets: Array<{ p1: number; p2: number }>;
  status: 'scheduled' | 'in_progress' | 'completed';
  table: string;
  startTime: string;
  sourceMatchIds?: string[];
};

type LevelTab = {
  id: string | number;
  slug: string;
  display_name: string;
};

type AnnouncementItem = {
  id: number;
  type: 'text' | 'text_image' | 'image' | 'video';
  title: string;
  body?: string;
  imageUrl?: string;
  videoUrl?: string;
  author: string;
  time: string;
  pinned?: boolean;
};

type AnnouncementFilter = 'all' | 'pinned' | 'media';

const MOCK_WAITLIST = [
  { name: 'Radu Nistor', rank: 143, points: 1224 },
  { name: 'Andrei Lungu', rank: 155, points: 1189 },
  { name: 'Mihai Dinu', rank: 167, points: 1140 },
  { name: 'Catalin Teodoru', rank: 173, points: 1119 },
];

const DEFAULT_LEVEL_TABS: LevelTab[] = [
  { id: 'hobby', slug: 'hobby', display_name: 'Hobby' },
  { id: 'avansati', slug: 'avansati', display_name: 'Avansati' },
  { id: 'open', slug: 'open', display_name: 'Open' },
  { id: 'elite', slug: 'elite', display_name: 'Elite' },
];

const MOCK_ANNOUNCEMENTS: AnnouncementItem[] = [
  {
    id: 1,
    type: 'text',
    title: 'Check-in începe la 08:00',
    body: 'Vă rugăm să ajungeţi cu cel puţin 30 de minute înainte de primul meci pentru ridicarea ecusonării şi desemnarea zonei de încălzire.',
    author: 'Biroul Turneu',
    time: 'Astăzi, 07:20',
    pinned: true,
  },
  {
    id: 2,
    type: 'text_image',
    title: 'Actualizare dispunere mese',
    body: 'Mesele 1-4 sunt acum desemnate pentru calificările Open/Elite. Vedeţi harta mai jos înainte de slot.',
    imageUrl: 'https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?auto=format&fit=crop&w=1200&q=80',
    author: 'Echipa de Operaţiuni',
    time: 'Astăzi, 09:05',
  },
  {
    id: 3,
    type: 'image',
    title: 'Atmosferă Live',
    imageUrl: 'https://images.unsplash.com/photo-1560272564-c83b66b1ad12?auto=format&fit=crop&w=1200&q=80',
    author: 'Echipa Media',
    time: 'Astăzi, 10:32',
  },
  {
    id: 4,
    type: 'video',
    title: 'Replay briefing de deschidere',
    body: 'Aţi ratat briefing-ul de deschidere? Urmăriţi actualizările cheie şi noteśi arbitraj aici.',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    author: 'Arbitru Principal',
    time: 'Astăzi, 11:00',
  },
];

const asNum = (v: unknown, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const hashCode = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const nextBracketSize = (count: number, maxParticipants?: number) => {
  const sizes = [8, 16, 32, 64, 128, 256];
  const target = Math.max(count, asNum(maxParticipants, count));
  return sizes.find((s) => s >= target) ?? 256;
};

const roundName = (roundIndex: number, totalRounds: number) => {
  if (roundIndex === totalRounds - 1) return 'Finala';
  if (roundIndex === totalRounds - 2) return 'Semifinale';
  if (roundIndex === totalRounds - 3) return 'Sferturi de finală';
  return `Runda ${roundIndex + 1}`;
};

const formatSetsSummary = (sets: Array<{ p1: number; p2: number }>) => {
  let p1Wins = 0;
  let p2Wins = 0;
  sets.forEach((s) => {
    if (s.p1 > s.p2) p1Wins += 1;
    else p2Wins += 1;
  });
  return `${p1Wins}-${p2Wins}`;
};

const getStatusTone = (status: string) => {
  switch (status) {
    case 'open':
      return 'status-open';
    case 'ongoing':
      return 'status-ongoing';
    case 'full':
      return 'status-full';
    case 'completed':
      return 'status-completed';
    case 'cancelled':
      return 'status-cancelled';
    default:
      return 'status-draft';
  }
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'TBD';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return 'TBD';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getInitials = (firstName: string, lastName: string) => `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

const getBracketSpacing = (roundIndex: number, totalRounds: number) => {
  const matchHeight = 120;
  const baseGap = 16;
  const unit = matchHeight + baseGap; // 136
  
  if (roundIndex === 0) {
    return { gap: baseGap, offset: 0 };
  }
  
  // Gap is the space BETWEEN matches (not including match height)
  // For proper alignment: gap = 2^round * unit - matchHeight
  const gap = Math.pow(2, roundIndex) * unit - matchHeight;
  
  // Offset positions the first match to align with midpoint of previous round pair
  // offset = unit * 2^(round-1) - unit/2
  const offset = unit * Math.pow(2, roundIndex - 1) - unit / 2;
  
  return { gap, offset };
};

const createMockDraw = (seedKey: string, names: string[], size: number) => {
  const rounds = Math.log2(size);
  const seeded = [...names];
  while (seeded.length < size) seeded.push('BYE');

  const draw: DrawMatch[][] = [];
  let currentRoundNames = seeded;
  let currentRoundSourceMatchIds: Array<string | null> = Array.from({ length: seeded.length }, () => null);

  for (let round = 0; round < rounds; round += 1) {
    const roundMatches: DrawMatch[] = [];
    const winners: string[] = [];
    const nextRoundSourceMatchIds: string[] = [];
    const isEarlyRound = round < 2;

    for (let i = 0; i < currentRoundNames.length; i += 2) {
      const p1 = currentRoundNames[i] ?? 'TBD';
      const p2 = currentRoundNames[i + 1] ?? 'TBD';
      const matchId = `${seedKey}-${round}-${i}`;
      const slotIndex = i / 2;
      const seed = hashCode(`${seedKey}-${round}-${i}-${p1}-${p2}`);
      const completed = isEarlyRound ? seed % 3 !== 0 : seed % 6 === 0;
      const p1Wins = seed % 2 === 0;
      const sets: Array<{ p1: number; p2: number }> = [];

      if (completed && p2 !== 'BYE') {
        const setCount = 3 + (seed % 3); // 3..5
        for (let s = 0; s < setCount; s += 1) {
          const base = 8 + ((seed + s) % 4);
          const p1Set = p1Wins ? 11 : base;
          const p2Set = p1Wins ? base : 11;
          sets.push({ p1: p1Set, p2: p2Set });
        }
      }

      const scoreLabel = completed
        ? p2 === 'BYE'
          ? 'WO'
          : formatSetsSummary(sets)
        : seed % 5 === 0
          ? 'LIVE'
          : 'TBD';

      const status: DrawMatch['status'] = completed
        ? 'completed'
        : seed % 5 === 0
          ? 'in_progress'
          : 'scheduled';

      const winner = p2 === 'BYE' ? p1 : p1Wins ? p1 : p2;
      winners.push(completed || p2 === 'BYE' ? winner : 'TBD');
      nextRoundSourceMatchIds.push(matchId);

      const sourceMatchIds = round === 0
        ? undefined
        : [currentRoundSourceMatchIds[i], currentRoundSourceMatchIds[i + 1]]
            .filter((value): value is string => Boolean(value));

      roundMatches.push({
        id: matchId,
        roundIndex: round,
        slotIndex,
        p1,
        p2,
        scoreLabel,
        completed: completed || p2 === 'BYE',
        winner: p2 === 'BYE' ? 'p1' : completed ? (p1Wins ? 'p1' : 'p2') : null,
        sets,
        status,
        table: `T${((seed % 16) + 1).toString()}`,
        startTime: `${(9 + (seed % 10)).toString().padStart(2, '0')}:${(seed % 2) ? '30' : '00'}`,
        sourceMatchIds,
      });
    }

    draw.push(roundMatches);
    currentRoundNames = winners;
    currentRoundSourceMatchIds = nextRoundSourceMatchIds;
  }

  return draw;
};

const Tournament: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState<TournamentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [selectedLevelTab, setSelectedLevelTab] = useState<string>('all');
  const [selectedDrawTab, setSelectedDrawTab] = useState<string>('all');
  const [announcementFilter, setAnnouncementFilter] = useState<AnnouncementFilter>('all');
  const [activeMedia, setActiveMedia] = useState<AnnouncementItem | null>(null);
  const [activeMatch, setActiveMatch] = useState<{ match: DrawMatch; roundIndex: number; totalRounds: number } | null>(null);
  const [highlightedMatchIds, setHighlightedMatchIds] = useState<string[]>([]);

  const loadTournamentDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const response = await tournamentsAPI.getById(parseInt(id, 10));
      if (response.success && response.data) {
        setTournament(response.data);
      } else {
        setError('Tournament not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load tournament details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTournamentDetails();
  }, [loadTournamentDetails]);

  const handleJoinTournament = async () => {
    if (!tournament || joining) return;

    try {
      setJoining(true);
      const response = await tournamentsAPI.join(tournament.id);
      if (!response.success) {
        alert(response.message || 'Failed to join tournament');
        return;
      }
      await loadTournamentDetails();
    } catch (err: any) {
      alert(err.message || 'An error occurred');
    } finally {
      setJoining(false);
    }
  };

  const participants = useMemo(() => tournament?.participants || [], [tournament?.participants]);
  const slots = asNum(tournament?.max_participants, 0);
  const isFull = Boolean(slots && participants.length >= slots);
  const canJoin = Boolean(
    tournament &&
      !tournament.is_registered &&
      tournament.status === 'open' &&
      (!slots || participants.length < slots)
  );

  const levelTabs = useMemo<LevelTab[]>(() => {
    const allowed = tournament?.allowed_levels || [];
    if (allowed.length > 0) {
      return allowed.map((lvl) => ({ id: lvl.id, slug: lvl.slug, display_name: lvl.display_name }));
    }

    const participantLevels = Array.from(
      new Set(
        participants
          .map((p) => p.level_slug)
          .filter((slug): slug is string => Boolean(slug))
      )
    );

    if (participantLevels.length > 0) {
      return participantLevels.map((slug) => ({
        id: slug,
        slug,
        display_name: slug.charAt(0).toUpperCase() + slug.slice(1),
      }));
    }

    return DEFAULT_LEVEL_TABS;
  }, [participants, tournament?.allowed_levels]);

  useEffect(() => {
    const validTabs = new Set(['all', ...levelTabs.map((l) => l.slug)]);
    if (!validTabs.has(selectedLevelTab)) {
      setSelectedLevelTab('all');
    }
    if (!validTabs.has(selectedDrawTab)) {
      setSelectedDrawTab('all');
    }
  }, [levelTabs, selectedDrawTab, selectedLevelTab]);

  const filteredParticipants = useMemo(() => {
    if (selectedLevelTab === 'all') return participants;
    return participants.filter((p) => p.level_slug === selectedLevelTab);
  }, [participants, selectedLevelTab]);

  const waitingList = useMemo(() => {
    if (!isFull) return [] as TournamentParticipant[];
    if (!slots) return [] as TournamentParticipant[];
    return participants.slice(slots);
  }, [isFull, slots, participants]);

  const shouldMockInProgress = useMemo(() => {
    if (!tournament) return false;
    if (tournament.status === 'ongoing') return true;
    return tournament.status === 'open' && tournament.id % 2 === 0;
  }, [tournament]);

  const drawByLevel = useMemo(() => {
    if (!tournament) return {} as Record<string, DrawMatch[][]>;

    const byLevel: Record<string, DrawMatch[][]> = {};
    const buildForKey = (key: string, row: TournamentParticipant[]) => {
      const names = row.map((p) => `${p.first_name} ${p.last_name}`).slice(0, 128);
      const bracketSize = nextBracketSize(names.length || 8, tournament.max_participants);
      byLevel[key] = createMockDraw(`${tournament.id}-${key}`, names.length ? names : ['TBD Player'], bracketSize);
    };

    buildForKey('all', participants);
    levelTabs.forEach((lvl) => {
      const scoped = participants.filter((p) => p.level_slug === lvl.slug);
      buildForKey(lvl.slug, scoped);
    });

    return byLevel;
  }, [levelTabs, participants, tournament]);

  const matchPathByTab = useMemo(() => {
    const rounds = drawByLevel[selectedDrawTab] || [];
    const allMatches = rounds.flat();
    const pathMap: Record<string, string[]> = {};

    const walkToFinal = (startId: string) => {
      const path: string[] = [startId];
      let cursor = startId;

      for (let round = 1; round < rounds.length; round += 1) {
        const nextMatch = rounds[round].find((m) => (m.sourceMatchIds || []).includes(cursor));
        if (!nextMatch) break;
        path.push(nextMatch.id);
        cursor = nextMatch.id;
      }

      return path;
    };

    allMatches.forEach((m) => {
      pathMap[m.id] = walkToFinal(m.id);
    });

    return pathMap;
  }, [drawByLevel, selectedDrawTab]);

  useEffect(() => {
    setHighlightedMatchIds([]);
    setActiveMatch(null);
  }, [selectedDrawTab]);

  const filteredAnnouncements = useMemo(() => {
    if (announcementFilter === 'all') return MOCK_ANNOUNCEMENTS;
    if (announcementFilter === 'pinned') return MOCK_ANNOUNCEMENTS.filter((a) => a.pinned);
    return MOCK_ANNOUNCEMENTS.filter((a) => a.type === 'text_image' || a.type === 'image' || a.type === 'video');
  }, [announcementFilter]);

  const openMedia = (item: AnnouncementItem) => {
    if (item.type === 'image' || item.type === 'text_image' || item.type === 'video') {
      setActiveMedia(item);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container page-inner">
          <div className="tt-loading">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Se încarcă</span></div>
            <span>Se încarcă detaliile turneului...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="page">
        <div className="container page-inner">
          <div className="alert alert-danger">{error || 'Turneu negăsit'}</div>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/tournaments')}>Inapoi la turnee</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container page-inner">
        <button className="btn btn-link p-0 mb-3" style={{ textDecoration: 'none' }} onClick={() => navigate('/tournaments')}>
          Inapoi la turnee
        </button>

        <div className="tour-hero mb-4">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
                <span className={`badge ${getStatusTone(tournament.status)}`}>{tournament.status}</span>
                {shouldMockInProgress && <span className="badge bg-danger">Live / In Desfasurare</span>}
                {(tournament.tournament_type_name || tournament.category) && (
                  <span className="badge bg-primary">{tournament.tournament_type_name || tournament.category}</span>
                )}
              </div>
              <h1 className="mb-2">{tournament.title}</h1>
              <p className="mb-0 text-muted">{tournament.description || 'Pagina oficiala a evenimentului'}</p>
            </div>

            <div className="tour-cta-box">
              {tournament.is_registered ? (
                <>
                  <div className="tour-joined">Inscris</div>
                  <div className="small text-muted">Status: {tournament.registration_status || 'inregistrat'}</div>
                </>
              ) : canJoin ? (
                <button className="btn btn-primary" disabled={joining} onClick={handleJoinTournament}>
                  {joining ? 'Se inscrie...' : 'Inscrie-te la turneu'}
                </button>
              ) : (
                <div className="small text-muted">Inregistrarea nu este disponibila momentan</div>
              )}
            </div>
          </div>

          <div className="row mt-3 g-3">
            <div className="col-md-3 col-6">
              <div className="tour-stat"><span>Inceput</span><strong>{formatDate(tournament.start_date)}</strong></div>
            </div>
            <div className="col-md-3 col-6">
              <div className="tour-stat"><span>Final</span><strong>{formatDate(tournament.end_date)}</strong></div>
            </div>
            <div className="col-md-3 col-6">
              <div className="tour-stat"><span>Inscrieri</span><strong>{participants.length}{slots ? ` / ${slots}` : ''}</strong></div>
            </div>
            <div className="col-md-3 col-6">
              <div className="tour-stat"><span>Puncte</span><strong>C {asNum(tournament.points_win)} / E {asNum(tournament.points_tie)} / I {asNum(tournament.points_loss)}</strong></div>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-8">
            <div className="entity-card h-100">
              <div className="entity-card-body">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h5 className="mb-0">Participanti</h5>
                  {levelTabs.length > 0 && (
                    <ul className="nav nav-pills tour-level-tabs">
                      <li className="nav-item"><button className={`nav-link ${selectedLevelTab === 'all' ? 'active' : ''}`} onClick={() => setSelectedLevelTab('all')}>Toti</button></li>
                      {levelTabs.map((lvl) => (
                        <li className="nav-item" key={lvl.id}>
                          <button className={`nav-link ${selectedLevelTab === lvl.slug ? 'active' : ''}`} onClick={() => setSelectedLevelTab(lvl.slug)}>{lvl.display_name}</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {filteredParticipants.length === 0 ? (
                  <p className="text-muted mb-0">Nicun participant inca pentru acest nivel.</p>
                ) : (
                  <div className="row g-3">
                    {filteredParticipants.map((p) => (
                      <div key={p.id} className="col-md-6">
                        <div className="tour-player-card">
                          <div className="avatar" style={{ width: 36, height: 36, fontSize: '.72rem' }}>
                            {getInitials(p.first_name, p.last_name)}
                          </div>
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{p.first_name} {p.last_name}</div>
                            <div className="small text-muted">{p.level_name || 'Nivel nespecificat'}</div>
                          </div>
                          <div className="text-end">
                            <div className="small text-muted">{p.level_name || '—'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="entity-card h-100">
              <div className="entity-card-body">
                <h5 className="mb-3">Lista de asteptare</h5>
                {!isFull ? (
                  <p className="text-muted mb-0">Lista de asteptare se activeaza cand evenimentul este complet.</p>
                ) : waitingList.length > 0 ? (
                  <div className="d-grid gap-2">
                    {waitingList.map((p) => (
                      <div key={`w-${p.id}`} className="tour-wait-item">
                        <span>{p.first_name} {p.last_name}</span>
                        <small>{p.level_name || '—'}</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="d-grid gap-2">
                    {MOCK_WAITLIST.map((m, idx) => (
                      <div className="tour-wait-item" key={`${m.name}-${idx}`}>
                        <span>{m.name}</span>
                        <small>#{m.rank} / {m.points} MP</small>
                      </div>
                    ))}
                    <div className="small text-muted mt-2">Previzualizare lista de asteptare (mock pentru UX)</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="entity-card mb-4">
          <div className="entity-card-body">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="mb-0">Anunturi</h5>
              <div className="d-flex align-items-center gap-2">
                <ul className="nav nav-pills tour-ann-filters">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${announcementFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setAnnouncementFilter('all')}
                    >
                      Toate
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${announcementFilter === 'pinned' ? 'active' : ''}`}
                      onClick={() => setAnnouncementFilter('pinned')}
                    >
                      Fixate
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${announcementFilter === 'media' ? 'active' : ''}`}
                      onClick={() => setAnnouncementFilter('media')}
                    >
                      Media
                    </button>
                  </li>
                </ul>
                <span className="badge bg-light text-dark">Feed de test</span>
              </div>
            </div>

            <div className="tour-ann-grid">
              {filteredAnnouncements.map((item) => (
                <article key={item.id} className={`tour-ann-card type-${item.type}`}>
                  <div className="tour-ann-head">
                    <div>
                      <h6>{item.title}</h6>
                      <div className="tour-ann-meta">{item.author} • {item.time}</div>
                    </div>
                    {item.pinned && <span className="badge bg-warning text-dark">Fixat</span>}
                  </div>

                  {item.body && <p>{item.body}</p>}

                  {(item.type === 'text_image' || item.type === 'image') && item.imageUrl && (
                    <button className="tour-media-btn" onClick={() => openMedia(item)}>
                      <img src={item.imageUrl} alt={item.title} className="tour-ann-image" loading="lazy" />
                    </button>
                  )}

                  {item.type === 'video' && item.videoUrl && (
                    <button className="tour-media-btn" onClick={() => openMedia(item)}>
                      <div className="tour-ann-video-wrap">
                        <iframe
                          src={item.videoUrl}
                          title={item.title}
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </button>
                  )}
                </article>
              ))}
            </div>

            {filteredAnnouncements.length === 0 && (
              <div className="text-muted" style={{ fontSize: '.9rem' }}>
                Nici un anunt in acest filtru inca.
              </div>
            )}
          </div>
        </div>

        <div className="entity-card">
          <div className="entity-card-body">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="mb-0">Trageri & Tabele</h5>
              {levelTabs.length > 0 && (
                <ul className="nav nav-pills tour-level-tabs">
                  <li className="nav-item"><button className={`nav-link ${selectedDrawTab === 'all' ? 'active' : ''}`} onClick={() => setSelectedDrawTab('all')}>Toate</button></li>
                  {levelTabs.map((lvl) => (
                    <li className="nav-item" key={`draw-${lvl.id}`}>
                      <button className={`nav-link ${selectedDrawTab === lvl.slug ? 'active' : ''}`} onClick={() => setSelectedDrawTab(lvl.slug)}>{lvl.display_name}</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bracket-container">
              <div className="bracket-scroll">
                {(drawByLevel[selectedDrawTab] || []).map((round, roundIndex, allRounds) => {
                  const spacing = getBracketSpacing(roundIndex, allRounds.length);
                  const hasNext = roundIndex < allRounds.length - 1;
                  
                  return (
                    <div className="bracket-round" key={`round-${roundIndex}`}>
                      <div className="bracket-round-header">
                        <h6>{roundName(roundIndex, allRounds.length)}</h6>
                      </div>
                      
                      <div 
                        className="bracket-matches"
                        style={{
                          gap: `${spacing.gap}px`,
                          marginTop: `${spacing.offset}px`,
                          ['--bracket-match-gap' as any]: `${spacing.gap}px`
                        }}
                      >
                        {round.map((match, idx) => {
                          const isEven = idx % 2 === 0;
                          const isHighlighted = highlightedMatchIds.includes(match.id);
                          const isLive = match.status === 'in_progress';
                          
                          return (
                            <div
                              key={match.id}
                              className={`bracket-match-wrapper ${hasNext ? 'has-connector' : ''} ${isEven ? 'even' : 'odd'} ${isHighlighted ? 'is-highlighted' : ''}`}
                            >
                              <button
                                type="button"
                                className={`bracket-match ${isLive ? 'is-live' : ''} ${isHighlighted ? 'is-highlighted' : ''}`}
                                onClick={() => {
                                  setActiveMatch({ match, roundIndex, totalRounds: allRounds.length });
                                  setHighlightedMatchIds(matchPathByTab[match.id] || [match.id]);
                                }}
                              >
                                <div className={`bracket-player ${match.winner === 'p1' ? 'winner' : ''}`}>
                                  <span className="player-name">{match.p1}</span>
                                </div>
                                <div className={`bracket-player ${match.winner === 'p2' ? 'winner' : ''}`}>
                                  <span className="player-name">{match.p2}</span>
                                </div>
                                <div className={`bracket-match-info ${match.completed ? 'completed' : ''}`}>
                                  <span className="score">{match.scoreLabel}</span>
                                  <span className="details">{match.table} • {match.startTime}</span>
                                </div>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {activeMedia && (
          <div className="tour-media-modal" role="dialog" aria-modal="true" onClick={() => setActiveMedia(null)}>
            <div className="tour-media-content" onClick={(e) => e.stopPropagation()}>
              <button className="tour-media-close" onClick={() => setActiveMedia(null)} aria-label="Close media viewer">
                Close
              </button>
              <div className="tour-media-head">
                <h4>{activeMedia.title}</h4>
                <div>{activeMedia.author} • {activeMedia.time}</div>
              </div>

              {(activeMedia.type === 'image' || activeMedia.type === 'text_image') && activeMedia.imageUrl && (
                <img src={activeMedia.imageUrl} alt={activeMedia.title} className="tour-media-full" />
              )}

              {activeMedia.type === 'video' && activeMedia.videoUrl && (
                <div className="tour-media-video-full">
                  <iframe
                    src={activeMedia.videoUrl}
                    title={activeMedia.title}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeMatch && (
          <div className="tour-media-modal" role="dialog" aria-modal="true" onClick={() => setActiveMatch(null)}>
            <div className="tour-media-content tour-match-modal" onClick={(e) => e.stopPropagation()}>
              <button className="tour-media-close" onClick={() => setActiveMatch(null)} aria-label="Close match details">
                Close
              </button>
              <div className="tour-media-head">
                <h4>{roundName(activeMatch.roundIndex, activeMatch.totalRounds)} • Match Details</h4>
                <div>{activeMatch.match.table} • {activeMatch.match.startTime}</div>
              </div>

              <div className="tour-match-detail-head">
                <div className={activeMatch.match.winner === 'p1' ? 'winner' : ''}>{activeMatch.match.p1}</div>
                <span>{activeMatch.match.scoreLabel}</span>
                <div className={activeMatch.match.winner === 'p2' ? 'winner' : ''}>{activeMatch.match.p2}</div>
              </div>

              <div className="tour-sets-grid">
                {activeMatch.match.sets.length > 0 ? (
                  activeMatch.match.sets.map((setScore, idx) => (
                    <div className="tour-set-row" key={`${activeMatch.match.id}-set-${idx}`}>
                      <span>Set {idx + 1}</span>
                      <strong>{setScore.p1}</strong>
                      <strong>{setScore.p2}</strong>
                    </div>
                  ))
                ) : (
                  <div className="text-muted" style={{ fontSize: '.9rem' }}>
                    Set details will appear once the match starts.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tournament;
